import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Switch, FormControlLabel, Divider, Select, MenuItem,
  FormControl, InputLabel, Button, CircularProgress, Alert, Chip,
} from '@mui/material';
import TelegramIcon from '@mui/icons-material/Telegram';
import {
  getTelegramStatus, getTelegramLinkCode, sendTelegramTest, unlinkTelegram, TelegramStatus,
} from '../services/api';

// ──────────────────────────────────────────────────────────────────────────
// Telegram Alerts — Phase 2 of the platform spine (test channel). Links this
// account to a Telegram chat via a short-lived code + the bot's /start
// command, so breaking-pattern events on watched names can be pushed here.
// ──────────────────────────────────────────────────────────────────────────
const TelegramAlerts: React.FC = () => {
  const [status, setStatus] = useState<TelegramStatus | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; severity: 'success' | 'error' | 'info' } | null>(null);

  const refresh = useCallback(() => {
    getTelegramStatus().then(setStatus).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Poll while a code is showing, so the UI flips to "linked" the moment the
  // user messages the bot — no manual refresh needed.
  useEffect(() => {
    if (!code || status?.linked) return;
    const id = setInterval(refresh, 3000);
    return () => clearInterval(id);
  }, [code, status?.linked, refresh]);

  const handleGetCode = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await getTelegramLinkCode();
      setCode(r.code);
    } catch (e: any) {
      setMsg({ text: e?.response?.data?.detail || 'Failed to generate a code', severity: 'error' });
    }
    setBusy(false);
  };

  const handleTest = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await sendTelegramTest();
      setMsg(r.sent ? { text: 'Test alert sent — check Telegram.', severity: 'success' }
                     : { text: 'Telegram accepted the request but did not confirm delivery.', severity: 'info' });
    } catch (e: any) {
      setMsg({ text: e?.response?.data?.detail || 'Failed to send test alert', severity: 'error' });
    }
    setBusy(false);
  };

  const handleUnlink = async () => {
    setBusy(true); setMsg(null);
    try {
      await unlinkTelegram();
      setCode(null);
      setMsg({ text: 'Unlinked. You will no longer receive Telegram alerts.', severity: 'info' });
      refresh();
    } catch {
      setMsg({ text: 'Failed to unlink', severity: 'error' });
    }
    setBusy(false);
  };

  if (!status) return <CircularProgress size={20} />;

  if (!status.configured) {
    return (
      <Alert severity="info">
        Telegram push isn't configured on the server yet (no bot token set). Ask an admin to
        create a bot via @BotFather and add <code>TELEGRAM_BOT_TOKEN</code> to the environment.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Chip
          size="small"
          label={status.linked ? 'Linked' : 'Not linked'}
          color={status.linked ? 'success' : 'default'}
        />
        {status.bot_username && (
          <Typography variant="body2" color="text.secondary">
            Bot: @{status.bot_username}
          </Typography>
        )}
      </Box>

      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      {status.linked ? (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            You'll get a push here when a watched name breaks out with Edge B or better.
          </Typography>
          <Button size="small" variant="outlined" onClick={handleTest} disabled={busy}>
            Send test alert
          </Button>
          <Button size="small" color="error" variant="text" onClick={handleUnlink} disabled={busy}>
            Unlink
          </Button>
        </Box>
      ) : code ? (
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            1. Open Telegram and message <b>@{status.bot_username}</b><br />
            2. Send: <code style={{ fontSize: 15, padding: '2px 6px', background: 'rgba(127,127,127,0.15)', borderRadius: 4 }}>/start {code}</code><br />
            3. This page updates automatically once you're linked (code expires in 10 minutes).
          </Typography>
          <Button size="small" variant="text" onClick={handleGetCode} disabled={busy}>
            Generate a new code
          </Button>
        </Box>
      ) : (
        <Button variant="contained" startIcon={<TelegramIcon />} onClick={handleGetCode} disabled={busy}>
          {busy ? <CircularProgress size={18} /> : 'Connect Telegram'}
        </Button>
      )}
    </Box>
  );
};

const Settings = () => {
  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Settings
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <TelegramIcon sx={{ fontSize: 20, mr: 1, verticalAlign: 'text-bottom', color: '#2AABEE' }} />
          Telegram Alerts
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <TelegramAlerts />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Display Preferences</Typography>
        <Divider sx={{ mb: 2 }} />

        <FormControlLabel
          control={<Switch defaultChecked />}
          label="Dark Mode (Coming Soon)"
          disabled
        />

        <Box sx={{ mt: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Currency</InputLabel>
            <Select defaultValue="USD" label="Currency">
              <MenuItem value="USD">USD ($)</MenuItem>
              <MenuItem value="EUR">EUR (€)</MenuItem>
              <MenuItem value="GBP">GBP (£)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Data Settings</Typography>
        <Divider sx={{ mb: 2 }} />

        <FormControlLabel
          control={<Switch defaultChecked />}
          label="Auto-refresh portfolio data"
        />

        <Box sx={{ mt: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Refresh Interval</InputLabel>
            <Select defaultValue="60" label="Refresh Interval">
              <MenuItem value="30">30 seconds</MenuItem>
              <MenuItem value="60">1 minute</MenuItem>
              <MenuItem value="300">5 minutes</MenuItem>
              <MenuItem value="600">10 minutes</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>About</Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" color="textSecondary">
          Finance Tool v1.0.0
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          A comprehensive portfolio management and analytics platform.
        </Typography>
      </Paper>
    </Box>
  );
};

export default Settings;
